import { Modal, Form, Select, Input, message } from 'antd';
import AxiosOICStat from "AxiosOICStat";
import { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import BinaryFormulaBuilder from '../BinaryFormulaBuilder';

const { Option } = Select;

const IloIndicatorMappingModal = ({ visible, onCancel, onSuccess, record }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [indicatorMappings, setIndicatorMappings] = useState([]);
  const [indicatorCodes, setIndicatorCodes] = useState([]);
  const [countryMappings, setCountryMappings] = useState([]);
  const indicatorsState = useSelector((state) => state.indicators);

  const availableIndicators = indicatorsState.value
    ? Object.values(indicatorsState.value).sort((a, b) => {
      const aName = a.ind_name_eng || '';
      const bName = b.ind_name_eng || '';
      return aName.localeCompare(bName);
    })
    : [];

  const fetchMappings = async () => {
    try {
      const [mappingsRes, countryRes, codesRes] = await Promise.all([
        AxiosOICStat.get("/external_api/ilo/indicator-mappings"),
        AxiosOICStat.get("/external_api/ilo/country-mappings"),
        AxiosOICStat.get("/external_api/ilo/indicator-mapping-codes")
      ]);
      setIndicatorMappings(mappingsRes.data.mappings || []);
      setCountryMappings(countryRes.data.mappings || []);
      setIndicatorCodes(codesRes.data.items || []);
    } catch (err) {
      console.warn(err);
    }
  };

  const uniqueValues = useMemo(() => {
    const sets = {
      ilo_code: new Set(),
      concanaete_code: new Set(),
      type: new Set(),
      sex: new Set(),
      class_one: new Set(),
      class_two: new Set(),
      formulation: new Set(),
    };

    indicatorMappings.forEach(m => {
      if (m.ilo_code) sets.ilo_code.add(m.ilo_code);
      if (m.concanaete_code) sets.concanaete_code.add(m.concanaete_code);
      if (m.type) sets.type.add(m.type);
      if (m.sex) sets.sex.add(m.sex);
      if (m.class_one) sets.class_one.add(m.class_one);
      if (m.class_two) sets.class_two.add(m.class_two);
      if (m.formulation) sets.formulation.add(m.formulation);
    });

    return Object.fromEntries(
      Object.entries(sets).map(([key, set]) => [key, Array.from(set).sort()])
    );
  }, [indicatorMappings]);

  const augmentedUniqueValues = useMemo(() => {
    try {
      const extra = new Set();

      const knownFull = new Set();
      (uniqueValues.concanaete_code || []).forEach(x => x && knownFull.add(String(x)));
      (uniqueValues.ilo_code || []).forEach(x => x && knownFull.add(String(x)));
      (indicatorCodes || []).forEach(it => { if (it && it.indicator) knownFull.add(String(it.indicator)); });
      (availableIndicators || []).forEach(it => { if (it && it.ind_code) knownFull.add(String(it.ind_code)); });

      (indicatorMappings || []).forEach(m => {
        const f = m.formulation || '';
        if (!f) return;
        const mm = String(f).match(/^([A-Za-z0-9_]+)\s*[+\-\*\/]\s*([A-Za-z0-9_]+)$/);
        const sides = mm ? [mm[1], mm[2]] : (String(f).split(/[+\-\*\/]/).map(s=>s.trim()).filter(Boolean));
        sides.forEach(side => {
          if (!side) return;
          const parts = side.split('_').filter(Boolean);
          for (let n = 1; n <= Math.min(3, parts.length); n++) {
            const candidate = parts.slice(-n).join('_');
            if (!candidate) continue;
            if (/^\d+$/.test(candidate)) continue;
            if (candidate.length <= 1) continue;
            if (knownFull.has(candidate)) continue;
            extra.add(candidate);
          }
        });
      });

      const candidates = Array.from(extra).sort();
      const toRemove = new Set();
      for (let i = 0; i < candidates.length; i++) {
        for (let j = 0; j < candidates.length; j++) {
          if (i === j) continue;
          const a = candidates[i];
          const b = candidates[j];
          if (b.endsWith(`_${a}`)) {
            toRemove.add(a);
          }
        }
      }
      const filtered = candidates.filter(c => !toRemove.has(c));

      return {
        ...uniqueValues,
        class_one: Array.from(new Set([...(uniqueValues.class_one || []), ...filtered])).sort(),
        class_two: Array.from(new Set([...(uniqueValues.class_two || []), ...filtered])).sort(),
      };
    } catch (e) {
      return uniqueValues;
    }
  }, [indicatorMappings, uniqueValues]);

  useEffect(() => {
    if (visible) {
      fetchMappings();
    }
  }, [visible]);

  // When opened in edit mode (record provided), populate form with record values
  useEffect(() => {
      if (visible && record) {
      try {
        form.setFieldsValue({
          ind_code:String(record.ind_code),
          ilo_code: record.ilo_code,
          concanaete_code: record.concanaete_code,
          type: record.type,
          formulation: record.formulation,
          multiplier: record.multiplier,
          sex: record.sex,
          class_one: record.class_one,
          class_two: record.class_two,
        });
      } catch (e) {}
    }
    if (!visible && !record) {
      form.resetFields();
    }
  }, [visible, record, form]);
 

  const iloCode = Form.useWatch('ilo_code', form);
  const sex = Form.useWatch('sex', form);
  const classOne = Form.useWatch('class_one', form);
  const classTwo = Form.useWatch('class_two', form);
  const mappingType = Form.useWatch('type', form);

  const isFormulationType = (val) => {
    if (!val) return false;
    try {
      return String(val).toLowerCase().includes('formul') || String(val).toLowerCase().includes('formula');
    } catch (e) {
      return false;
    }
  };

  useEffect(() => {
    try {
      // debug logs removed
    } catch (e) {}
  }, [mappingType]);

  useEffect(() => {
    const parts = [iloCode, sex, classOne, classTwo].filter(Boolean);
    form.setFieldsValue({ concanaete_code: parts.join('_') });
  }, [iloCode, sex, classOne, classTwo, form]);


  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      if (record && record.id) {
        // update existing mapping
        await AxiosOICStat.put(`/external_api/ilo/indicator-mappings/${record.id}`, values);
        message.success("Indicator mapping updated successfully");
      } else {
        await AxiosOICStat.post("/external_api/ilo/indicator-mappings", values);
        message.success("Indicator mapping added successfully");
      }
      form.resetFields();
      onSuccess();
    } catch (err) {
      if (err.errorFields) {
        return;
      }
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        'Failed to add indicator mapping. Please try again.';
      message.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={record ? "Edit ILO Indicator Mapping" : "Add ILO Indicator Mapping"}
      visible={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      width={600}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="ind_code"
          label="OIC Indicator Code"
          rules={[{ required: true, message: "Please select an indicator" }]}
        >
          <Select
            showSearch
            autoClearSearchValue={false}
            placeholder="Select an indicator"
            filterOption={(input, option) => {
              const child = (option && option.children) ?? (option && option.label) ?? '';
              let text = '';
              if (typeof child === 'string') text = child;
              else if (Array.isArray(child)) text = child.join('');
              else if (child && child.props && child.props.children) text = String(child.props.children);
              else text = String(child || '');
              return text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
            }}
          >
            {availableIndicators.map(indicator => (
              <Option key={String(indicator.ind_code)} value={String(indicator.ind_code)}>
                {indicator.ind_code} - {indicator.ind_name_eng}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="ilo_code"
          label="ILO Code"
          required
          rules={[
            { required: true, message: 'Please enter ILO code' },
            {
              validator: (_, value) => {
                if (form.getFieldValue('type') === 'formulation') return Promise.resolve();
                const hasValue = !!(value && String(value).trim());
                if (!hasValue) return Promise.reject(new Error('Please enter ILO code'));
                const currentSex = (form.getFieldValue('sex') || '').toString().trim();
                const exists = indicatorMappings.some(m => {
                  if (record && String(m.id) === String(record.id)) return false;
                  if (!m.ilo_code || String(m.ilo_code).trim() !== String(value).trim()) return false;
                  const otherSex = (m.sex || '').toString().trim();
                  return otherSex === currentSex;
                });
                if (exists) return Promise.reject(new Error('An indicator mapping with this ILO Code and Sex already exists'));
                return Promise.resolve();
              }
            }
          ]}
        >
          <Select showSearch autoClearSearchValue={false} placeholder="Select or enter ILO indicator code">
            {indicatorCodes.length > 0 ? (
              indicatorCodes.map(item => (
                <Option key={item.indicator} value={item.indicator}>
                  {item.indicator} {item.label ? ` - ${item.label}` : ''}
                </Option>
              ))
            ) : (
              uniqueValues.ilo_code.map(val => (
                <Option key={val} value={val}>{val}</Option>
              ))
            )}
          </Select>
        </Form.Item>

        <Form.Item
          name="concanaete_code"
          label="Concatenated Code"
        >
          <Input disabled placeholder="Auto-generated from other fields" />
        </Form.Item>
        <Form.Item
          name="type"
          label="Type"
          rules={[{ required: true, message: "Please enter type" }]}
        >
          <Select showSearch autoClearSearchValue={false} placeholder="Select or enter type">
            {uniqueValues.type.map(val => (
              <Option key={val} value={val}>{val}</Option>
            ))}
          </Select>
        </Form.Item>
        {isFormulationType(mappingType) && (
          <>
            <BinaryFormulaBuilder
              indicatorCodes={indicatorCodes}
              availableIndicators={availableIndicators}
              uniqueValues={augmentedUniqueValues}
              form={form}
              initialFormula={record ? record.formulation : ''}
              onChange={(val) => {
                try { form.setFieldsValue({ formulation: val }); } catch (e) {}
              }}
            />
            <Form.Item
              name="formulation"
              label="Formulation"
              required
              rules={[
                { required: true, message: 'Please enter formulation' },
                {
                  validator: (_, value) => {
                    if (!isFormulationType(form.getFieldValue('type'))) return Promise.resolve();
                    const v = String(value || '').trim();
                    if (!v) return Promise.resolve();
                    const invalidChars = v.replace(/[A-Za-z0-9_\,\s\+\-\*\/\(\)]/g, '');
                    if (invalidChars.length > 0) return Promise.reject(new Error(`Invalid characters detected: ${invalidChars}`));
                    if (/[+\-\*\/]{2,}/.test(v)) return Promise.reject(new Error('Consecutive operators detected'));
                    let open = 0;
                    for (let ch of v) {
                      if (ch === '(') open++;
                      else if (ch === ')') {
                        open--;
                        if (open < 0) return Promise.reject(new Error('Unbalanced parentheses: closing before opening'));
                      }
                    }
                    if (open > 0) return Promise.reject(new Error('Unbalanced parentheses: missing closing parenthesis'));
                    if (/^[+\*\/]/.test(v)) return Promise.reject(new Error('Formula cannot start with operator (except -)'));
                    if (/[+\-\*\/]$/.test(v)) return Promise.reject(new Error('Formula cannot end with an operator'));
                    const known = new Set();
                    (uniqueValues.concanaete_code || []).forEach(x => x && known.add(String(x)));
                    (uniqueValues.formulation || []).forEach(x => x && known.add(String(x)));
                    (uniqueValues.ilo_code || []).forEach(x => x && known.add(String(x)));
                    (augmentedUniqueValues.class_one || []).forEach(x => x && known.add(String(x)));
                    (augmentedUniqueValues.class_two || []).forEach(x => x && known.add(String(x)));
                    (augmentedUniqueValues.sex || []).forEach(x => x && known.add(String(x)));
                    (indicatorCodes || []).forEach(it => { if (it && it.indicator) known.add(String(it.indicator)); });
                    (availableIndicators || []).forEach(it => { if (it && it.ind_code) known.add(String(it.ind_code)); });

                    const normalize = s => String(s || '').replace(/\s+/g, '').toLowerCase();
                    const unchanged = record && normalize(record.formulation) === normalize(v);
                    if (!unchanged && known.size > 0) {
                      const tokens = v.match(/[A-Za-z0-9_]+/g) || [];
                      const missing = [];
                      const knownBases = new Set();
                      (uniqueValues.concanaete_code || []).forEach(x => x && knownBases.add(String(x)));
                      (uniqueValues.ilo_code || []).forEach(x => x && knownBases.add(String(x)));
                      (indicatorCodes || []).forEach(it => { if (it && it.indicator) knownBases.add(String(it.indicator)); });
                      (availableIndicators || []).forEach(it => { if (it && it.ind_code) knownBases.add(String(it.ind_code)); });

                      const augClassOne = new Set((augmentedUniqueValues.class_one || []).map(x => String(x)));
                      const augClassTwo = new Set((augmentedUniqueValues.class_two || []).map(x => String(x)));
                      const augSex = new Set((augmentedUniqueValues.sex || []).map(x => String(x)));
                      const augAll = new Set([...augClassOne, ...augClassTwo, ...augSex]);

                      const isCompositeValid = (tok) => {
                        if (known.has(tok)) return true;
                        const parts = String(tok).split('_').filter(Boolean);
                        if (parts.length <= 1) return false;

                        for (let split = 1; split < parts.length; split++) {
                          const baseCand = parts.slice(0, split).join('_');
                          if (!knownBases.has(baseCand)) continue;
                          let remainder = parts.slice(split);
                          while (remainder.length > 0) {
                            let matched = false;
                            for (let len = Math.min(3, remainder.length); len >= 1; len--) {
                              const cand = remainder.slice(-len).join('_');
                              if (augAll.has(cand)) {
                                remainder = remainder.slice(0, remainder.length - len);
                                matched = true;
                                break;
                              }
                            }
                            if (!matched) break;
                          }
                          if (remainder.length === 0) return true;
                        }
                        return false;
                      };

                      tokens.forEach(t => {
                        if (/^\d+$/.test(t) || t.length <= 1) return;
                        if (!known.has(t) && !isCompositeValid(t)) missing.push(t);
                      });
                      if (missing.length) return Promise.reject(new Error(`Unknown token(s): ${missing.join(', ')}`));
                    }

                    const exists = indicatorMappings.some(m => m.formulation && String(m.formulation).trim() === v && (!record || String(m.id) !== String(record.id)));
                    if (exists) return Promise.reject(new Error('An indicator mapping with this Formulation already exists'));

                    return Promise.resolve();
                  }
                }
                ]}
                validateTrigger={["onChange", "onBlur"]}
            >
                <Input.TextArea autoSize={{ minRows: 2, maxRows: 3 }} placeholder="Enter formulation"/>
            </Form.Item>
          </>
        )}
        <Form.Item
          name="multiplier"
          label="Multiplier"
          initialValue={1}
        >
          <Select style={{ width: 160 }}>
            <Option value={0.001}>Multiply ×0.001</Option>
            <Option value={0.01}>Multiply ×0.01</Option>
            <Option value={0.1}>Multiply ×0.1</Option>
            <Option value={1}>No multiply</Option>
            <Option value={10}>Multiply ×10</Option>
            <Option value={100}>Multiply ×100</Option>
            <Option value={1000}>Multiply ×1000</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="sex"
          label="Sex"
        >
          <Select showSearch autoClearSearchValue={false} allowClear placeholder="Select or enter sex">
            {(augmentedUniqueValues.sex || []).map(val => (
              <Option key={val} value={val}>{val}</Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="class_one"
          label="Class One"
        >
          <Select showSearch autoClearSearchValue={false} allowClear placeholder="Select or enter class one">
            {(augmentedUniqueValues.class_one || []).map(val => (
              <Option key={val} value={val}>{val}</Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="class_two"
          label="Class Two"
        >
          <Select showSearch autoClearSearchValue={false} allowClear placeholder="Select or enter class two">
            {(augmentedUniqueValues.class_two || []).map(val => (
              <Option key={val} value={val}>{val}</Option>
            ))}
          </Select>
        </Form.Item>
        
      </Form>
    </Modal>
  );
};

export default IloIndicatorMappingModal;